// ==================== 主应用入口 ====================

import * as THREE from 'three';
import { io, Socket } from 'socket.io-client';
import { SceneManager } from './core/SceneManager.js';
import { NPCController } from './core/NPCController.js';
import type { SceneConfig } from './types/index.js';

let sceneManager: SceneManager;
let npcController: NPCController;
let socket: Socket | null = null;

const npcNames: Record<string, string> = {
  npc_guide: '向导小灵',
  npc_poet: '诗人墨白',
  npc_scholar: '学者博言',
  npc_jester: '小丑欢欢',
};

async function main(): Promise<void> {
  const container = document.getElementById('app')!;
  const loadingEl = document.getElementById('loading')!;
  const hudEl = document.getElementById('hud')!;
  const dialogEl = document.getElementById('dialog-input')!;
  const chatPanelEl = document.getElementById('chat-panel')!;

  // WebGL 检测
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  if (!gl) {
    loadingEl.textContent = '您的浏览器不支持 WebGL，无法渲染 3D 场景。请使用 Chrome/Firefox/Edge 等现代浏览器，并确保已启用硬件加速。';
    loadingEl.style.color = '#ff6b6b';
    return;
  }

  sceneManager = new SceneManager(container);
  npcController = sceneManager.getNPCController();

  const defaultScene: SceneConfig = {
    id: 'scene_default',
    name: '默认舞台',
    panoramaPath: '/panoramas/stage-panorama.jpg',
    cameraInitial: [0, 8, 12],
    cameraLookAt: [0, 0, 0],
    lighting: {
      ambient: { color: '#404060', intensity: 0.6 },
      directional: { color: '#ffffff', intensity: 1.0, position: [10, 20, 5] },
    },
    npcs: [
      { id: 'npc_guide', name: '向导小灵', role: '舞台向导', spawnPosition: [0, 0, 5], spawnRotation: 180, color: '#4a90e2' },
      { id: 'npc_poet', name: '诗人墨白', role: '浪漫主义诗人', spawnPosition: [-5, 0, 3], spawnRotation: 45, color: '#8e44ad' },
      { id: 'npc_scholar', name: '学者博言', role: '百科全书学者', spawnPosition: [4, 0, 4], spawnRotation: -30, color: '#27ae60' },
      { id: 'npc_jester', name: '小丑欢欢', role: '舞台开心果', spawnPosition: [-3, 0, -3], spawnRotation: 150, color: '#e74c3c' },
    ],
  };

  await sceneManager.loadScene(defaultScene);

  loadingEl.style.display = 'none';
  hudEl.style.display = 'flex';
  dialogEl.style.display = 'flex';
  chatPanelEl.style.display = 'flex';

  bindHUD();
  bindDialogInput();
  connectSocket();

  console.log('[App] 全景图NPC舞台已初始化');
}

function bindHUD(): void {
  const btnFree = document.getElementById('btn-camera-free')!;
  const btnTop = document.getElementById('btn-camera-top')!;
  const btnNpc = document.getElementById('btn-camera-npc')!;
  const btnGrid = document.getElementById('btn-toggle-grid')!;
  const btnReset = document.getElementById('btn-reset')!;

  btnFree.addEventListener('click', () => {
    sceneManager.setCameraMode('free');
    setActiveButton(btnFree, [btnTop, btnNpc]);
  });
  btnTop.addEventListener('click', () => {
    sceneManager.setCameraMode('top-down');
    setActiveButton(btnTop, [btnFree, btnNpc]);
  });
  btnNpc.addEventListener('click', () => {
    const firstNpc = npcController.getNPCStates()[0];
    if (firstNpc) sceneManager.setCameraMode('follow-npc', firstNpc.id);
    setActiveButton(btnNpc, [btnFree, btnTop]);
  });
  btnGrid.addEventListener('click', () => {
    sceneManager.toggleGrid();
    btnGrid.classList.toggle('active');
  });
  btnReset.addEventListener('click', () => window.location.reload());
}

function setActiveButton(active: HTMLElement, others: HTMLElement[]): void {
  active.classList.add('active');
  others.forEach((btn) => btn.classList.remove('active'));
}

function bindDialogInput(): void {
  const npcSelect = document.getElementById('npc-select') as HTMLSelectElement;
  const input = document.getElementById('user-input') as HTMLInputElement;
  const btn = document.getElementById('send-btn')!;

  const send = () => {
    const text = input.value.trim();
    if (!text || !socket?.connected) return;
    socket.emit('user_message', { text, npcId: npcSelect.value });
    input.value = '';
  };

  btn.addEventListener('click', send);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') send();
  });

  npcSelect.addEventListener('change', () => {
    input.placeholder = `对 ${npcNames[npcSelect.value] || 'NPC'} 说点什么...`;
  });
  input.placeholder = '对 向导小灵 说点什么...';
}

function addChatMessage(sender: 'user' | 'npc', npcId: string, text: string): void {
  const chatList = document.getElementById('chat-list')!;
  const npcName = npcNames[npcId] || npcId;
  const item = document.createElement('div');
  item.className = `chat-item ${sender}`;
  item.innerHTML = `
    <div class="chat-sender">${sender === 'user' ? '你' : npcName}</div>
    <div class="chat-text">${escapeHtml(text)}</div>
  `;
  chatList.appendChild(item);
  chatList.scrollTop = chatList.scrollHeight;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function connectSocket(): void {
  // 优先使用环境变量配置的后端地址，否则回退到同源
  const backendUrl = import.meta.env.VITE_API_URL || window.location.origin;

  socket = io(backendUrl, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
  });

  socket.on('connect', () => {
    console.log('[Socket] 已连接:', socket!.id);
    updateConnectionStatus('connected');
    socket!.emit('join_stage');
  });

  socket.on('npc_dialog_bubble', (data: { npcId: string; text: string; duration?: number }) => {
    npcController.showDialogBubble(data.npcId, data.text, data.duration ?? 5000);
    addChatMessage('npc', data.npcId, data.text);
  });

  socket.on('npc_update', (data: { npcId: string; updates: Record<string, unknown> }) => {
    npcController.updateNPC(data.npcId, data.updates);
  });

  socket.on('user_message_sent', (data: { npcId: string; text: string }) => {
    addChatMessage('user', data.npcId, data.text);
  });

  socket.on('stage_sync', (data: Record<string, unknown>) => {
    console.log('[Stage]', data);
  });

  socket.on('disconnect', () => {
    console.log('[Socket] 断开，自动重连中...');
    updateConnectionStatus('disconnected');
  });
}

/** 连接状态指示器 */
function updateConnectionStatus(status: 'connected' | 'disconnected'): void {
  const el = document.getElementById('conn-status');
  if (!el) return;
  el.className = 'conn-status ' + status;
  el.textContent = status === 'connected' ? '已连接' : '已断开';
}

main().catch((err) => {
  console.error('应用初始化失败', err);
  document.getElementById('loading')!.textContent = '初始化失败，请刷新页面重试。';
});
